// Turn a detected cross-capability ambiguity into a CONFIRM card: Otto asks which capability the user meant
// (filter / chart / navigate) instead of guessing. Each choice carries a `directive` — an unambiguous
// re-phrasing the frontend re-submits through the normal flow, so the chosen capability runs its own
// grounded pipeline (the classifier routes "filter by region" / "group the chart by region" / "open the
// regions page" cleanly, with no new prompt rules). Confirm-before-act, exactly like the nav/chart cards.
import { pageActionLabel, pageReference, isTableViewTarget } from "../planner/pageManifest";

const cap = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// A destination mid-sentence: table views are proper names ("Buildings Table View"), everything else takes
// the natural article ("the Asset Management page"). Same rule as capabilityRedirect's `prose`.
const prose = target => (isTableViewTarget(target) ? pageReference(target) : `the ${pageReference(target)}`);

// capability -> { label (button text), directive (utterance we re-submit) }. `singular`/`plural` come from
// the detected term. Directives are the canonical phrasings the classifier already routes without ambiguity.
// The filter builder also takes the term's `mode`: an EXACT dimension ("region") re-submits a real directive;
// an entity noun (LOOSE, e.g. "recommendations") can't be filtered by literally — the noun IS the dataset — so
// its choice carries a `clarify` question instead of a directive, and the pick asks WHAT to filter by.
const CHOICE_BUILDERS = {
    filter: (singular, plural, mode) =>
        mode === "loose"
            ? {
                  label: `Filter by ${cap(plural)}`,
                  clarify: `What would you like to filter the ${plural} by? For example a trade, a system, criticality, a cost threshold, or a region.`
              }
            : {
                  label: `Filter by ${cap(singular)}`,
                  directive: `filter by ${singular}`
              },
    chart: (singular, plural) => ({
        label: `Group the chart by ${cap(singular)}`,
        directive: `group the chart by ${singular}`
    }),
    navigate: (singular, plural) => ({
        label: `Go to the ${cap(plural)} page`,
        directive: `open the ${plural} page`
    })
};

// detection = { term, singular, plural, capabilities: ["filter","chart","navigate"] } from
// detectCrossCapabilityAmbiguity. interpretResponse threads the request/session id so the turn stays traced
// and the card gets a feedback bar. ctx = { utterance }.
export const buildCapabilityClarifyProposal = (interpretResponse, ctx = {}, detection) => {
    const { singular, plural, capabilities } = detection;
    const choices = (capabilities || [])
        .map(capability => {
            const build = CHOICE_BUILDERS[capability];
            if (!build) return null;
            // Only the filter choice varies by match strength (exact directive vs. entity-noun clarification).
            const mode = capability === "filter" ? detection.filterMode : undefined;
            const choice = { capability, ...build(singular, plural, mode) };
            // The nav choice navigates DIRECTLY on click (the pick IS the confirmation) — carry its grounded
            // target so the card drives onNavigate instead of re-submitting a directive that asks again. Its
            // BUTTON is named off the real target so a data-grid reads "Go to Buildings Table View" (the
            // directive stays "open the buildings page" — that's the internal phrase the classifier re-routes).
            //
            // A noun can name SEVERAL destinations (assets = the Asset Dashboard AND the register), so this
            // expands into one button per destination, best-first. Offering only the top one hid a page the
            // user plainly meant; each still navigates directly, so no extra confirmation is introduced.
            if (capability === "navigate") {
                const targets = detection.navTargets || (detection.navTarget ? [detection.navTarget] : []);
                if (targets.length) {
                    return targets.map(target => ({ ...choice, target, label: pageActionLabel("Go to", target) }));
                }
            }
            return choice;
        })
        .filter(Boolean)
        .reduce((all, c) => all.concat(c), []);

    return {
        crossCapability: true,
        needsClarification: true,
        term: detection.term,
        choices,
        // A LONE navigate choice isn't a "which did you mean?" — there is only one thing Otto can do with the
        // noun here, and asking as if there were several reads as a stall. Say where the records live (which
        // teaches the platform) and offer to go, exactly like the capability-redirect seam's single-candidate
        // shape. The multi-choice wording is untouched.
        message:
            detection.navOnly && detection.navTarget
                ? `${cap(plural)} live on ${prose(detection.navTarget)}. Want me to take you there?`
                : `Sure — I can work with ${plural} a few ways. Which did you mean?`,
        requestId: interpretResponse && interpretResponse.request_id,
        sessionId: interpretResponse && interpretResponse.session_id,
        utterance: ctx.utterance
    };
};
